import React from 'react'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useApolloClient } from 'hooks/useApolloClient'
// components
import Wysiwyg from 'components/FormElements/Wysiwyg'
import ControlButtons from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/ControlButtons'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IField, IFieldResponse, IFieldUpdateMutationArgs } from 'types/graphql'
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
// constants
import { Modals } from 'constants/modals'
// views
import { DragHandle, LabelField, LabelWrapper } from './views'
// graphql
import { FIELD_UPDATE_MUTATION } from 'graphql/objects/mutations'
// utils
import { debounce } from 'lodash'
import { prepareHtml } from 'utils/html'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

interface Props {
  field: IField
  dragHandleProps: DraggableProvidedDragHandleProps
}

const Label: React.FC<Props> = ({ field, dragHandleProps }) => {
  const { client } = useApolloClient()

  const { isRisk, isRiskTemplate, deleteField, refetchTemplate } = React.useContext(RiskTemplateAPIContext)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const updateFieldLabel = async (label: string) => {
    try {
      if (isRisk || field.label === label) {
        return
      }

      await client.mutate<IFieldResponse, IFieldUpdateMutationArgs>({
        mutation: FIELD_UPDATE_MUTATION,
        variables: {
          id: field.id,
          input: {
            label: field.wysiwyg ? prepareHtml(label) : label
          }
        }
      })

      await refetchTemplate()
    } catch (errors) {
      console.log('[Label.updateFieldLabel]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const onChange = React.useCallback(
    debounce((label: string) => updateFieldLabel(label), 600),
    [isRisk, field.wysiwyg]
  )

  const onDeleteClick = () => {
    showModal({
      width: 'sm',
      entity: 'field',
      handleConfirm: () => deleteField(field)
    })
  }

  return (
    <LabelWrapper {...(isRiskTemplate && dragHandleProps)} $editable={isRiskTemplate}>
      {field.wysiwyg ? (
        <>
          <DragHandle {...dragHandleProps} />
          <Wysiwyg onChange={onChange} value={field.label} readOnly={!isRiskTemplate} />
        </>
      ) : (
        <LabelField>{field.label}</LabelField>
      )}
      {isRiskTemplate && <ControlButtons field={field} onDeleteClick={onDeleteClick} />}
    </LabelWrapper>
  )
}

export default Label
